import NotificationDropdown from '../NotificationDropdown/NotificationDropdown';
import { useNotificationListener } from '../hooks/NotificationContext';
import { useEffect, useRef, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { useAuth } from '../../auth/Auth';
import { useNavigate } from 'react-router-dom';
import { usePoints } from '../hooks/pointsContext';
import { useLogo } from '../hooks/LogoContext';
import { ButtonSpinner } from '../UI/ButtonSpinner/ButtonSpinner';
import { formatCurrentLongDate } from '../utils/utils';

const BellSvg = () => (
  <svg
    width={14}
    height={14}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden
  >
    <path d='M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9' />
    <path d='M13.73 21a2 2 0 01-3.46 0' />
  </svg>
);

const ProfileSvg = () => (
  <svg
    width={14}
    height={14}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden
  >
    <circle cx='12' cy='8' r='4' />
    <path d='M6 21v-1a4 4 0 014-4h4a4 4 0 014 4v1' />
  </svg>
);

const Topbar = ({ openSidebar }: { openSidebar?: () => void }) => {
  const notificationRef = useRef<HTMLDivElement>(null);

  const { currentUser } = useAuth();
  const { points, refreshPoints, loading } = usePoints();
  const navigate = useNavigate();
  const { notifications, markAsSeen, seenNotificationCount, resetPagination } =
    useNotificationListener();
  const [isNotificationOpen, setNotificationOpen] = useState(false);
  const { logoUrl, logoLoading } = useLogo();

  const bellRef = useRef<HTMLButtonElement>(null);

  const firstName = currentUser?.user?.firstName?.trim() || '';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const localDate = formatCurrentLongDate();

  const toggleNotifications = () => {
    resetPagination();
    markAsSeen();
    setNotificationOpen((prev) => !prev);
  };

  useEffect(() => {
    refreshPoints();
  }, [refreshPoints]);

  const pointsVal = Number(points?.data?.points?.totalAvailable ?? 0).toLocaleString('en-US');
  const ticketsVal = Number(points?.data?.tickets?.totalAvailable ?? 0).toLocaleString('en-US');
  const hasUnseen = (seenNotificationCount ?? 0) > 0;

  return (
    <div className='topbar'>
      <div className='flex items-center gap-3 min-w-0 flex-1 mr-3'>
        <button
          type='button'
          className='tb-icon lg:hidden shrink-0'
          onClick={openSidebar}
          aria-label='Open menu'
        >
          <span className='text-[15px] leading-none font-semibold'>☰</span>
        </button>
        <button
          type='button'
          onClick={() => navigate('/dashboard')}
          className='hidden sm:flex lg:hidden shrink-0 items-center'
          aria-label='Home'
        >
          {!logoLoading && <img src={logoUrl} alt='' className='h-8' />}
        </button>
        <div className='min-w-0'>
          <div className='tb-hi truncate hidden sm:block'>
            {firstName ? `${greeting}, ${firstName}.` : greeting}
          </div>
          <div className='tb-date hidden sm:block'>{localDate}</div>
        </div>
      </div>

      <div className='tb-right shrink-0'>
        <button
          type='button'
          className='tb-pill'
          style={{
            background: 'var(--color-blue-tint)',
            borderColor: 'rgba(0,85,255,.2)',
          }}
          onClick={() => navigate('/wallet')}
        >
          <span className='tb-pill-val' style={{ color: 'var(--color-blue)' }}>
            {loading ? <ButtonSpinner className='text-[var(--color-blue)]' /> : pointsVal}
          </span>
          <span className='tb-pill-lbl' style={{ color: 'rgba(0,85,255,.6)' }}>
            pts
          </span>
        </button>

        <button
          type='button'
          className='tb-pill'
          style={{
            background: 'rgba(201,150,63,.1)',
            borderColor: 'rgba(201,150,63,.25)',
          }}
          onClick={() => navigate('/rent-drop')}
        >
          <span className='tb-pill-val' style={{ color: 'var(--color-gold)' }} id='tixTop'>
            {loading ? <ButtonSpinner className='text-[var(--color-gold)]' /> : ticketsVal}
          </span>
          <span className='tb-pill-lbl' style={{ color: 'rgba(201,150,63,.7)' }}>
            tickets
          </span>
        </button>

        <div className='relative'>
          <button
            ref={bellRef}
            type='button'
            className='tb-icon'
            onClick={toggleNotifications}
            aria-label='Notifications'
          >
            <BellSvg />
            {hasUnseen && <span className='tb-bell-dot' />}
          </button>
          {isNotificationOpen && (
            <ClickAwayListener
              onClickAway={(e) => {
                const target = e.target as Node;
                if (
                  bellRef.current?.contains(target) ||
                  notificationRef.current?.contains(target)
                ) {
                  return;
                }
                setNotificationOpen(false);
              }}
            >
              <NotificationDropdown
                isOpen={isNotificationOpen}
                onClose={() => setNotificationOpen(false)}
                notifications={notifications}
                containerRef={notificationRef}
              />
            </ClickAwayListener>
          )}
        </div>

        <button
          type='button'
          className='tb-icon'
          onClick={() => navigate('/profile')}
          title='Profile & settings'
          aria-label='Profile and settings'
        >
          <ProfileSvg />
        </button>
      </div>
    </div>
  );
};

export default Topbar;
